"use client";

import type {
  ApprovalRequestId,
  EnvironmentId,
  ProviderApprovalDecision,
  ThreadId,
} from "@t3tools/contracts";
import { MicIcon, MicOffIcon, PhoneOffIcon } from "lucide-react";
import { memo } from "react";

import { Button } from "~/components/ui/button";
import { Dialog, DialogDescription, DialogPopup, DialogTitle } from "~/components/ui/dialog";
import { Spinner } from "~/components/ui/spinner";
import { cn } from "~/lib/utils";
import type { PendingApproval } from "~/session-logic";
import { useVoiceConversation, type VoiceConversationStatus } from "~/state/voiceConversation";
import { ComposerPendingApprovalActions } from "./ComposerPendingApprovalActions";
import { ComposerPendingApprovalPanel } from "./ComposerPendingApprovalPanel";

export interface VoiceConversationOverlayProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId | null;
  readonly approval: PendingApproval | null;
  readonly pendingApprovalCount: number;
  readonly isRespondingToApproval: boolean;
  readonly onRespondToApproval: (
    requestId: ApprovalRequestId,
    decision: ProviderApprovalDecision,
  ) => Promise<unknown>;
}

interface StatusPresentation {
  readonly label: string;
  readonly hint: string;
}

function statusPresentation(
  status: VoiceConversationStatus,
  errorMessage: string | null,
  activeToolLabel: string | null,
): StatusPresentation {
  switch (status) {
    case "connecting":
      return { label: "Connecting…", hint: "Opening a live voice session." };
    case "listening":
      return { label: "Listening", hint: "Tap the microphone to speak." };
    case "recording":
      return { label: "Recording…", hint: "Tap again to send your message." };
    case "sending":
      return { label: "Sending…", hint: "Delivering your voice turn." };
    case "thinking":
      return activeToolLabel
        ? { label: `Aveda uses ${activeToolLabel}…`, hint: "Working on your request." }
        : { label: "Aveda is thinking…", hint: "Working on your request." };
    case "speaking":
      return { label: "Aveda is responding…", hint: "Playing the reply." };
    case "error":
      return {
        label: "Something went wrong",
        hint: errorMessage ?? "The voice session hit an error.",
      };
    case "closed":
      return { label: "Conversation ended", hint: "Close this panel to return to chat." };
    default:
      return { label: "", hint: "" };
  }
}

/** Ring color + animation for the central orb, reflecting the live status. */
function orbClassName(status: VoiceConversationStatus): string {
  switch (status) {
    case "recording":
      return "bg-destructive/12 ring-destructive/60 animate-pulse";
    case "speaking":
      return "bg-primary/12 ring-primary/60 animate-pulse";
    case "thinking":
    case "sending":
      return "bg-muted ring-border animate-pulse";
    case "error":
      return "bg-destructive/10 ring-destructive/40";
    case "listening":
      return "bg-primary/8 ring-primary/40";
    default:
      return "bg-muted ring-border";
  }
}

export const VoiceConversationOverlay = memo(function VoiceConversationOverlay(
  props: VoiceConversationOverlayProps,
) {
  const { open, onOpenChange, environmentId, threadId } = props;

  const controller = useVoiceConversation({
    environmentId,
    threadId,
    active: open,
    onRequestClose: () => onOpenChange(false),
  });

  const presentation = statusPresentation(
    controller.status,
    controller.errorMessage,
    controller.activeToolLabel,
  );
  const connecting = controller.status === "connecting";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        showCloseButton={false}
        className="sm:max-w-md"
        aria-label="Live voice conversation"
      >
        <div className="flex min-h-0 flex-col items-center gap-6 px-6 py-8">
          <div className="flex flex-col items-center gap-1 text-center">
            <DialogTitle className="text-lg">Live voice</DialogTitle>
            <DialogDescription>Talk to Aveda about this Auftrag.</DialogDescription>
          </div>

          {/* Orb */}
          <div className="flex flex-col items-center gap-3">
            <div
              className={cn(
                "flex size-32 items-center justify-center rounded-full ring-2 transition-colors duration-300",
                orbClassName(controller.status),
              )}
            >
              {connecting || controller.isBusy ? (
                <Spinner className="size-8 text-muted-foreground" />
              ) : (
                <MicIcon
                  className={cn(
                    "size-10",
                    controller.isRecording ? "text-destructive" : "text-muted-foreground",
                  )}
                />
              )}
            </div>
            <div className="flex flex-col items-center gap-0.5 text-center">
              <p className="font-medium text-base leading-none">{presentation.label}</p>
              <p className="text-muted-foreground text-sm">{presentation.hint}</p>
            </div>
          </div>

          {/* Live transcript */}
          <div className="flex max-h-48 w-full min-h-0 flex-col gap-3 overflow-y-auto">
            {controller.userTranscript.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">You</span>
                <p className="text-sm leading-relaxed">{controller.userTranscript}</p>
              </div>
            )}
            {controller.assistantText.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">Aveda</span>
                <p className="text-sm leading-relaxed">{controller.assistantText}</p>
              </div>
            )}
          </div>

          {props.approval ? (
            <div className="w-full rounded-lg border border-border/65 bg-muted/20">
              <ComposerPendingApprovalPanel
                approval={props.approval}
                pendingCount={props.pendingApprovalCount}
              />
              <div className="flex flex-wrap justify-end gap-2 px-4 pb-4">
                <ComposerPendingApprovalActions
                  requestId={props.approval.requestId}
                  isResponding={props.isRespondingToApproval}
                  onRespondToApproval={props.onRespondToApproval}
                />
              </div>
            </div>
          ) : null}

          {/* Controls */}
          <div className="flex items-center gap-3">
            <Button
              type="button"
              size="icon-lg"
              variant={controller.isMuted ? "secondary" : "outline"}
              aria-label={controller.isMuted ? "Unmute microphone" : "Mute microphone"}
              onClick={controller.toggleMute}
            >
              {controller.isMuted ? <MicOffIcon /> : <MicIcon />}
            </Button>

            <button
              type="button"
              disabled={!controller.canToggleTurn}
              onClick={controller.toggleTurn}
              aria-label={controller.isRecording ? "Send voice turn" : "Start speaking"}
              className={cn(
                "flex size-16 items-center justify-center rounded-full text-white shadow-lg outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
                controller.isRecording
                  ? "bg-destructive hover:bg-destructive/90"
                  : "bg-primary hover:bg-primary/90",
              )}
            >
              {controller.isBusy ? <Spinner className="size-6" /> : <MicIcon className="size-6" />}
            </button>

            <Button
              type="button"
              size="icon-lg"
              variant="destructive"
              aria-label="End voice conversation"
              onClick={controller.end}
            >
              <PhoneOffIcon />
            </Button>
          </div>
        </div>
      </DialogPopup>
    </Dialog>
  );
});
